import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { AgentForm, ChatListSheet, MyAgentsList } from '@web/app/features/agents/components';
import { updateCacheWithNewChat } from '@web/app/features/agents/utils';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { type WorkspaceContext, createNavigator, extractRouteParams } from '@web/app/shared/utils/route-utils';
import { Loader2 } from 'lucide-react';

import { useStartAgentChat } from '@codes/chatbots';
import { useWebCoreStore } from '@codes/web-core';

import { useMyAgents } from '../hooks/useMyAgents';

export const MyAgentsPage = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routeParams = extractRouteParams<WorkspaceContext>(useParams());
    const { workspaceId, projectId } = routeParams;
    const navigator = createNavigator(navigate, { workspaceId, projectId });

    const { profile } = useWebCoreStore();
    const startMyChat = useStartAgentChat();

    const [selectedAgentForHistory, setSelectedAgentForHistory] = useState<string | null>(null);
    const [isHistorySheetOpen, setIsHistorySheetOpen] = useState(false);
    const [isCreatingChat, setIsCreatingChat] = useState(false);

    const {
        agentId,
        isCreateMode,
        searchQuery,
        page,
        limit,
        agentsData,
        isAgentsLoading,
        selectedAgent,
        isAgentLoading,
        paginationRange,
        isFormVisible,
        formMode,
        handleAgentSelect,
        handleCreateNew,
        handleFormSuccess,
        handlePageChange,
        handleSearchChange,
    } = useMyAgents();

    const handleChatList = (agentId: string) => {
        setSelectedAgentForHistory(agentId);
        setIsHistorySheetOpen(true);
    };

    const handleChatSelect = (chatId: string) => {
        // TODO: 선택된 채팅으로 이동하는 로직 구현
        console.log('Navigate to chat:', chatId);
    };

    const handleNewChat = async (agentId: string) => {
        try {
            setIsCreatingChat(true);

            // 현재 선택된 agent 정보 가져오기
            const currentAgent = selectedAgent || agentsData?.data?.find(agent => agent.id === agentId);
            const agentName = currentAgent?.meta$?.nick ?? currentAgent?.id ?? t('agent.listItem.states.noName');
            const chatName = `Chat with ${agentName}`;

            const profile$ = {
                ...(profile?.sid && { sid: profile.sid }),
                ...(profile?.uid && { uid: profile.uid }),
                ...(profile?.$user?.gender && { gender: profile?.$user?.gender }),
                ...(profile?.$user?.name && { name: profile?.$user?.name }),
            };

            const newChat = await startMyChat.mutateAsync({
                name: chatName,
                profile$,
                agentId,
            });

            if (newChat.id) {
                navigator.navigateTo(`/my-agents/${agentId}/chats/${newChat.id}`);
                updateCacheWithNewChat(queryClient, 'myChatbotKeys', newChat);
            }
        } catch (error) {
            console.error('Error creating new chat:', error);
        } finally {
            setIsCreatingChat(false);
        }
    };

    return (
        <div className="bg-background flex-1 min-h-0 w-full flex flex-col">
            <div className="border-b border-detail-line text-lg font-semibold font-display py-3 px-8 max-md:py-4 max-md:px-4 max-md:bg-transparent max-md:border-none">
                {t('menu.ai.agent')}
            </div>
            <PageContainer
                noPadding
                className="flex-1 py-8 px-8 flex justify-center max-md:px-4 max-md:pt-0 animate-enter"
            >
                <div className="h-full grid grid-cols-[1fr_2fr] max-md:grid-cols-1 gap-6 w-full">
                    <MyAgentsList
                        agents={agentsData?.data || []}
                        isLoading={isAgentsLoading}
                        selectedAgentId={agentId || null}
                        searchQuery={searchQuery}
                        onAgentSelect={handleAgentSelect}
                        onCreateNew={handleCreateNew}
                        onSearchChange={handleSearchChange}
                        onChatHistory={handleChatList}
                        currentPage={page}
                        totalCount={agentsData?.total || 0}
                        pageSize={limit}
                        paginationRange={paginationRange}
                        onPageChange={handlePageChange}
                    />

                    {isFormVisible ? (
                        isAgentLoading && agentId ? (
                            <div className="flex items-center gap-2 w-full h-full justify-center">
                                <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                        ) : (
                            <AgentForm
                                key={`${formMode}-${agentId || 'new'}`}
                                mode={formMode as 'create' | 'edit'}
                                agentId={agentId || null}
                                agentData={selectedAgent || null}
                                onFormSuccess={handleFormSuccess}
                                onChatClick={handleNewChat}
                                isCreatingChat={isCreatingChat}
                            />
                        )
                    ) : (
                        <div className="h-full min-h-[302px] bg-card shadow-custom border border-chatbot-line rounded-2xl flex items-center justify-center">
                            <div className="text-center text-text-500 px-4">
                                <div className="text-lg font-medium mb-2">{t('agent.page.emptyState.title')}</div>
                                <div className="text-sm">{t('agent.page.emptyState.description')}</div>
                            </div>
                        </div>
                    )}
                </div>

                <ChatListSheet
                    agentId={selectedAgentForHistory}
                    isOpen={isHistorySheetOpen}
                    onOpenChange={setIsHistorySheetOpen}
                    onChatSelect={handleChatSelect}
                    workspaceId={workspaceId}
                    projectId={projectId}
                />
            </PageContainer>
        </div>
    );
};
